/**
 * Builds a stable cache key from a prefix and parameters object
 *
 * Creates a deterministic cache key by:
 * - Sorting parameters alphabetically
 * - URL-encoding values
 * - Joining with '&' separator
 *
 * This prevents cache collisions when different parameter combinations
 * could create the same string representation.
 *
 * @param prefix - Cache key prefix (e.g., 'adminFetchUsers')
 * @param params - Parameters object to serialize
 * @returns Stable cache key string
 *
 * @example
 * ```ts
 * buildCacheKey('adminFetchUsers', { limit: 10, page: 1, sortBy: 'name' })
 * // Returns: 'adminFetchUsers:limit=10&page=1&sortBy=name'
 * ```
 */
export function buildCacheKey(
    prefix: string,
    params: Record<string, string | number | boolean | null | undefined>,
): string {
    const serializedParams = Object.entries(params)
        // Filter out undefined/null values
        .filter(([, value]) => value !== undefined && value !== null)
        // Sort by key for deterministic output
        .sort(([a], [b]) => a.localeCompare(b))
        // Encode key-value pairs
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
        .join('&');

    return serializedParams ? `${prefix}:${serializedParams}` : prefix;
}
